import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { X, MessageSquarePlus } from 'lucide-react';
import { format, parseISO } from 'date-fns';

/**
 * Feedback History drill-through — same inline pattern as DashboardDrillThroughPanel,
 * but each row also lets the PM leave/edit a follow-up note for that session's feedback.
 */
export const FeedbackHistoryPanel = ({ title, rows = [], onRowClick, onSaveNote, onClose, testId = 'pd-feedback-history' }) => {
  const [editingId, setEditingId] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (row) => {
    setEditingId(row.id);
    setNoteValue(row.pm_followup_note || '');
  };

  const save = async (row) => {
    setSaving(true);
    try {
      await onSaveNote(row, noteValue);
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-2 border-lime/40" data-testid={testId}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
        <div>
          <CardTitle className="font-heading text-base">{title}</CardTitle>
          <CardDescription className="text-xs">{rows.length} matching record{rows.length !== 1 ? 's' : ''} — add a follow-up note for the trainer/partner</CardDescription>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid={`${testId}-close`}>
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length > 0 ? (
          <div className="max-h-96 overflow-y-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Trainer</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Comments</TableHead>
                  <TableHead className="min-w-[220px]">Follow-up Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} data-testid={`${testId}-row-${row.id}`}>
                    <TableCell className="text-sm cursor-pointer hover:text-primary" onClick={() => onRowClick && onRowClick(row)}>{row.title}</TableCell>
                    <TableCell className="text-sm">{row.project_title}</TableCell>
                    <TableCell className="text-sm">{row.trainer_name}</TableCell>
                    <TableCell className="text-sm">{row.course_title}</TableCell>
                    <TableCell className="text-sm">{row.rating_label}</TableCell>
                    <TableCell className="text-sm max-w-[220px]">{row.verbatims}</TableCell>
                    <TableCell>
                      {editingId === row.id ? (
                        <div className="flex flex-col gap-1.5 min-w-[220px]">
                          <Textarea
                            value={noteValue}
                            onChange={(e) => setNoteValue(e.target.value)}
                            placeholder="Follow-up action for the trainer/partner..."
                            className="text-xs min-h-[60px]"
                            autoFocus
                            data-testid={`${testId}-note-input-${row.id}`}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs" disabled={saving} onClick={() => save(row)} data-testid={`${testId}-note-save-${row.id}`}>
                              {saving ? 'Saving...' : 'Save'}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : row.pm_followup_note ? (
                        <button
                          onClick={() => startEdit(row)}
                          className="text-left text-xs text-muted-foreground hover:text-foreground max-w-[220px]"
                          data-testid={`${testId}-note-view-${row.id}`}
                        >
                          <p className="line-clamp-2">{row.pm_followup_note}</p>
                          {row.pm_followup_by && (
                            <p className="text-[11px] text-muted-foreground mt-1 pt-1 border-t border-dashed">
                              {row.pm_followup_by}{row.pm_followup_at ? ` · ${format(parseISO(row.pm_followup_at), 'MMM d, yyyy')}` : ''}
                            </p>
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => startEdit(row)}
                          className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                          data-testid={`${testId}-note-add-${row.id}`}
                        >
                          <MessageSquarePlus className="w-3.5 h-3.5" />
                          Add note
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8 text-sm">No matching records</p>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackHistoryPanel;
